import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl, getRawDateString, getUsersEmail, isAdminRole } from '../authConfig';
import { useTaxPortal } from '../utils/useTaxPortal';
import { toast } from 'react-toastify';
import TaxPortalLayout from '../components/Common/TaxPortalLayout';
import Loader from '../components/Common/Loader';
import CorporateCustomerDrawer from './CorporateCustomerDrawer';
import ActionItemsDrawer from './ActionItemsDrawer';
import { getValidToken } from '../utils/msalTokenUtil';
import {
  FaPhoneAlt, FaEdit, FaFolderOpen, FaCheckCircle,
  FaHourglassHalf, FaClipboardList, FaPaperPlane, FaFileSignature, FaExclamationCircle,
  FaMoneyBillWave, FaChevronLeft, FaChevronRight, FaInbox, FaSort, FaSortUp,
  FaSortDown, FaFileInvoiceDollar, FaEllipsisV, FaBuilding, FaUserPlus,
} from 'react-icons/fa';
import TaxPortalToolbar from '../components/Common/TaxPortalToolbar';
import { formatPhoneNumber, formatBusinessNumber } from '../utils/formatters';

const assignedToOptions = getUsersEmail();
const statusOptions = ['Open', 'Pending', 'Review', 'Draft Sent', 'eSigned', 'Payment Pending', 'Payment Received', 'Completed'];
const dueDateOptions = [];
for (let i = 0; i < 5; i++) {
  const date = new Date();
  date.setDate(date.getDate() + i);
  dueDateOptions.push(date.toISOString().split('T')[0]);
}

const STATUS_META = {
  Open:               { color: '#64748b', bg: '#f1f5f9', icon: FaFolderOpen },
  Pending:            { color: '#b45309', bg: '#fef3c7', icon: FaHourglassHalf },
  Review:             { color: '#1d4ed8', bg: '#dbeafe', icon: FaClipboardList },
  'Draft Sent':       { color: '#0e7490', bg: '#cffafe', icon: FaPaperPlane },
  eSigned:            { color: '#6d28d9', bg: '#ede9fe', icon: FaFileSignature },
  'Payment Pending':  { color: '#b91c1c', bg: '#fee2e2', icon: FaExclamationCircle },
  'Payment Received': { color: '#0f766e', bg: '#ccfbf1', icon: FaMoneyBillWave },
  Completed:          { color: '#15803d', bg: '#dcfce7', icon: FaCheckCircle },
};

const getStatusMeta = (status) => STATUS_META[status] || { color: '#64748b', bg: '#f1f5f9', icon: FaFolderOpen };

const StatusBadge = ({ status }) => {
  const meta = getStatusMeta(status);
  const Icon = meta.icon;
  return (
    <span
      className="d-inline-flex align-items-center gap-1 rounded-pill fw-semibold"
      style={{ color: meta.color, backgroundColor: meta.bg, fontSize: '0.75rem', padding: '5px 12px', lineHeight: 1.4, whiteSpace: 'nowrap' }}
    >
      <Icon size={10} />{status || 'Unknown'}
    </span>
  );
};

const SortIcon = ({ active, asc }) => {
  if (!active) return <FaSort className="ms-1 opacity-50" size={11} />;
  return asc ? <FaSortUp className="ms-1" size={11} /> : <FaSortDown className="ms-1" size={11} />;
};

// Avatar helpers (same palette as PersonalTaxDataPage)
const AVATAR_COLORS = [
  { bg: '#dbeafe', text: '#1e40af' }, { bg: '#fce7f3', text: '#9d174d' },
  { bg: '#d1fae5', text: '#065f46' }, { bg: '#ede9fe', text: '#5b21b6' },
  { bg: '#fef3c7', text: '#92400e' }, { bg: '#cffafe', text: '#164e63' },
  { bg: '#fee2e2', text: '#991b1b' }, { bg: '#e0f2fe', text: '#0c4a6e' },
];
const getAvatarColor = (name = '') => {
  const code = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};
const getInitials = (name = '') => {
  const clean = name.replace(/,/g, ' ').replace(/\band\b/gi, ' ');
  const parts = clean.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || 'B').substring(0, 2).toUpperCase();
};

const AVATAR_SIZE = 32;
const AVATAR_GAP  = 8;
const CELL_PS     = 16; // ps-3 = 1rem = 16px
const NAME_TH_PL  = CELL_PS + AVATAR_SIZE + AVATAR_GAP; // 56px

const BizAvatar = ({ name }) => {
  const c = getAvatarColor(name);
  return (
    <span
      className="name-avatar flex-shrink-0"
      style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, backgroundColor: c.bg, color: c.text }}
      aria-hidden="true"
    >
      {getInitials(name)}
    </span>
  );
};

// Smart due-date pill (reused from PersonalTaxDataPage pattern)
const DueDatePill = ({ dueDate, status }) => {
  if (status === 'Completed') return <span className="due-pill due-pill-normal">N/A</span>;
  if (!dueDate) return <span className="text-muted small">—</span>;
  try {
    const dueDateStr = new Date(dueDate).toISOString().split('T')[0];
    const todayStr   = new Date().toISOString().split('T')[0];
    if (dueDateStr === todayStr) {
      return (
        <span className="due-pill due-pill-today">
          <span className="pulse-dot" style={{ backgroundColor: '#d97706' }} />Today
        </span>
      );
    }
    if (dueDateStr < todayStr) {
      const diff = Math.round((new Date(todayStr) - new Date(dueDateStr)) / 86400000);
      return <span className="due-pill due-pill-overdue"><FaExclamationCircle size={9} />{diff}d overdue</span>;
    }
  } catch { }
  return <span className="due-pill due-pill-normal">{getRawDateString(dueDate)}</span>;
};

// Skeleton shimmer rows
const SkeletonRows = ({ count = 7, cols = 7 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <tr key={i} className="skeleton-row">
        <td style={{ paddingLeft: CELL_PS }}>
          <div className="d-flex align-items-center gap-2">
            <span className="skeleton-cell" style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, borderRadius: '50%' }} />
            <span className="skeleton-cell" style={{ width: 110 + (i % 3) * 20, height: 14 }} />
          </div>
        </td>
        {Array.from({ length: cols - 1 }).map((__, j) => (
          <td key={j}><span className="skeleton-cell" style={{ width: 70 + (j % 2) * 20, height: j === 1 ? 22 : 14, borderRadius: j === 1 ? 99 : 6 }} /></td>
        ))}
      </tr>
    ))}
  </>
);

const getId = (record) => record._id || record.id;

const compareDueDates = (a, b, key = 'dueDate') => {
  const aDate = a[key], bDate = b[key];
  if (!aDate && !bDate) return 0;
  if (!aDate) return 1;
  if (!bDate) return -1;
  const pA = new Date(aDate), pB = new Date(bDate);
  if (isNaN(pA.getTime()) || isNaN(pB.getTime())) return 0;
  return pA.getTime() - pB.getTime();
};

const TABS = [
  { key: 'Summary',    label: 'Summary' },
  { key: 'HST Filing', label: 'HST Filing' },
  { key: 'Payroll',    label: 'Payroll' },
];

const CorporateTaxDataPage = () => {
  const portalState = useTaxPortal();
  const navigate = useNavigate();
  const { // eslint-disable-line
    account, msalInstance,
    setDialNumber, isDialerOpen, setIsDialerOpen, setRefreshTrigger, setEmailModalConfig,
  } = portalState;

  const [records,          setRecords]          = useState([]);
  const [searchText,       setSearchText]       = useState('');
  const [sortKey,          setSortKey]          = useState('assignedTo');
  const [sortAsc,          setSortAsc]          = useState(true);
  const [filterAssignedTo, setFilterAssignedTo] = useState('');
  const [filterStatus,     setFilterStatus]     = useState('nonCompleted');
  const [filterDueDate,    setFilterDueDate]    = useState('');
  const [loading,          setLoading]          = useState(false);
  const [isSaving,         setIsSaving]         = useState(false);
  const [editingRecord,    setEditingRecord]    = useState(null);
  const [isDrawerOpen,     setIsDrawerOpen]     = useState(false);
  const [actionItemsCustomer, setActionItemsCustomer] = useState(null);
  const [pageSize,         setPageSize]         = useState(25);
  const [pageIndex,        setPageIndex]        = useState(0);
  const [activeTab,        setActiveTab]        = useState('Summary');

  const dialerRecords = useMemo(() => records.map(r => ({ ...r, name: r.businessName })), [records]);
  const isAdmin = isAdminRole(account?.username);

  useEffect(() => {
    document.body.classList.toggle('drawer-open', isDrawerOpen || !!actionItemsCustomer);
    return () => document.body.classList.remove('drawer-open');
  }, [isDrawerOpen, actionItemsCustomer]);

  useEffect(() => {
    if (account) fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const res = await fetch(getApiUrl('/v1/corporate'), {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Unable to load corporate customer data.');
      setRecords((await res.json()) || []);
      setRefreshTrigger(prev => prev + 1);
    } catch (e) {
      console.error(e);
      toast.error('Could not verify your session. Please sign in again.');
    } finally { setLoading(false); }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const handleSaveRecord = async (formData) => {
    setIsSaving(true);
    const isEditing = editingRecord && Object.keys(editingRecord).length > 0;
    const id = isEditing ? getId(editingRecord) : null;
    let payload = { 
      ...formData, 
      mobile: formatPhoneNumber(formData.mobile),
      businessNumber: formatBusinessNumber(formData.businessNumber),
      updatedBy: account?.username || account?.name || 'Unknown' 
    };
    if (!isEditing) {
      const cleanBizName = (payload.businessName || '').trim().toLowerCase();
      const cleanMobile = (payload.mobile || '').replace(/[^0-9]/g, '');
      const cleanBizNum = (payload.businessNumber || '').replace(/[^A-Z0-9]/gi, '').toUpperCase();
      const isDuplicate = records.some(r => {
        const rBizName = (r.businessName || '').trim().toLowerCase();
        const rMobile = (r.mobile || '').replace(/[^0-9]/g, '');
        const rBizNum = (r.businessNumber || '').replace(/[^A-Z0-9]/gi, '').toUpperCase();
        return rBizName === cleanBizName && (rMobile === cleanMobile || (cleanBizNum && rBizNum === cleanBizNum));
      });
      if (isDuplicate) {
        setIsSaving(false);
        toast.error('Corporate data already exists.');
        return;
      }
      if (!payload.dueDate) {
        const d = new Date(); d.setDate(d.getDate() + 3);
        payload.dueDate = d.toISOString().split('T')[0];
      }
    }
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const url    = isEditing ? getApiUrl(`/v1/corporate/${id}`) : getApiUrl('/v1/corporate');
      const method = isEditing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const b = await res.json(); throw new Error(b.message || 'Unable to save record.'); }
      const savedRecord = await res.json();
      if (isEditing) {
        setRecords(prev => prev.map(c => getId(c) === id ? { ...c, ...payload } : c));
        toast.success('Corporate record updated successfully.');
      } else {
        setRecords(prev => [{ ...savedRecord, ...payload }, ...prev]);
        toast.success('Corporate record added successfully.');
      }
      handleCloseDrawer();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Could not save record. Please try again.');
    } finally { setIsSaving(false); }
  };

  const handleDeleteRecord = async (record) => {
    const id = getId(record);
    setIsSaving(true);
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const res = await fetch(getApiUrl(`/v1/corporate/${id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) { const b = await res.json(); throw new Error(b.message || 'Unable to delete record.'); }
      setRecords(prev => prev.filter(item => getId(item) !== id));
      handleCloseDrawer();
      toast.success('Corporate record deleted.');
    } catch (e) {
      console.error(e);
      toast.error('Could not delete record. Please sign in again.');
    } finally { setIsSaving(false); }
  };

  const handleAssignedToChange = async (record, assignedTo) => {
    const id = getId(record);
    setIsSaving(true);
    const updates = { assignedTo, updatedBy: account?.username || account?.name || 'Unknown' };
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const res = await fetch(getApiUrl(`/v1/corporate/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(updates),
      });
      if (!res.ok) { const b = await res.json(); throw new Error(b.message || 'Unable to update assignment.'); }
      await res.json();
      setRecords(prev => prev.map(item => getId(item) === id ? { ...item, ...updates } : item));
      toast.success('Assignment updated.');
    } catch (e) {
      console.error(e);
      toast.error('Could not update assignment. Please sign in again.');
    } finally { setIsSaving(false); }
  };

  const handleOpenDrawerForAdd  = () => { setEditingRecord({}); setIsDrawerOpen(true); };
  const handleOpenDrawerForEdit = (r) => { setEditingRecord(r); setIsDrawerOpen(true); };
  const handleCloseDrawer       = () => { setEditingRecord(null); setIsDrawerOpen(false); };
  const handleOpenActionItems   = (customer) => setActionItemsCustomer(customer);

  const filteredData = records.filter((record) => {
    let assignedMatch    = filterAssignedTo ? record.assignedTo === filterAssignedTo : true;
    let tabSpecificMatch = true;
    let dueDateMatch     = filterDueDate ? record.dueDate === filterDueDate : true;
    let rawValue         = '';
    const currentStatus  = (activeTab === 'HST Filing' ? record.hstStatus : activeTab === 'Payroll' ? record.payrollStatus : record.status) || 'Pending';
    const statusMatch = filterStatus === 'nonCompleted'
      ? currentStatus.toLowerCase() !== 'completed'
      : filterStatus
        ? currentStatus.trim().toLowerCase() === filterStatus.trim().toLowerCase()
        : true;
    const currentUser = account?.username?.toLowerCase();
    if (currentUser && !isAdminRole(currentUser)) {
      assignedMatch = record.assignedTo?.toLowerCase() === currentUser;
    }
    if (activeTab === 'Summary') {
      rawValue = `${record.businessName} ${record.contactName} ${record.businessNumber} ${record.mobile} ${record.status} ${record.assignedTo} ${record.details} ${record.notes}`.toLowerCase();
    } else if (activeTab === 'HST Filing') {
      rawValue = `${record.businessName} ${record.contactName} ${record.businessNumber} ${record.hstStatus} ${record.assignedTo} ${record.hstNotes}`.toLowerCase();
      tabSpecificMatch = record.hstAvailable === 'Yes';
      dueDateMatch = filterDueDate ? record.hstDueDate === filterDueDate : true;
    } else {
      rawValue = `${record.businessName} ${record.contactName} ${record.businessNumber} ${record.payrollStatus} ${record.assignedTo}`.toLowerCase();
      tabSpecificMatch = record.payrollAvailable === 'Yes';
      dueDateMatch = filterDueDate ? record.payrollDueDate === filterDueDate : true;
    }
    return statusMatch && assignedMatch && tabSpecificMatch && dueDateMatch && rawValue.includes(searchText.toLowerCase());
  });

  const sortedData = [...filteredData].sort((a, b) => {
    const cu = account?.username?.toLowerCase();
    const aM = cu && a.assignedTo?.toLowerCase() === cu ? 0 : 1;
    const bM = cu && b.assignedTo?.toLowerCase() === cu ? 0 : 1;
    if (aM !== bM) return aM - bM;
    if (['dueDate', 'hstDueDate', 'payrollDueDate', 'payrollDateFiled'].includes(sortKey)) {
      const cmp = compareDueDates(a, b, sortKey); return sortAsc ? cmp : -cmp;
    }
    const aV = typeof a[sortKey] === 'string' ? a[sortKey].toLowerCase() : a[sortKey] || '';
    const bV = typeof b[sortKey] === 'string' ? b[sortKey].toLowerCase() : b[sortKey] || '';
    if (aV < bV) return sortAsc ? -1 : 1;
    if (aV > bV) return sortAsc ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const pageData   = sortedData.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPageIndex(0); }, [pageSize, filterStatus, filterAssignedTo, filterDueDate, searchText, activeTab, records.length]);

  const handleExportToExcel = async () => {
    const XLSX = await import('xlsx');
    const exportData = sortedData.map((r) => ({
      'Business Name': r.businessName || '', 'Contact Name': r.contactName || '',
      'Business Number': formatBusinessNumber(r.businessNumber), 'Phone number': formatPhoneNumber(r.mobile),
      'Email': r.email || '', 'Address': r.address || '',
      'Status': r.status || '',
      'Due Date': r.dueDate ? getRawDateString(r.dueDate) : '',
      'Assigned To': r.assignedTo || '', 'Year End': r.yearEnd || '',
      'Date Filed': r.dateFiled ? getRawDateString(r.dateFiled) : '',
      'Corporate Income Tax': r.corporateIncomeTax || '',
      'Invoice Amount': r.invoiceAmount || '', 'Invoice No.': r.invoiceNo || '',
      'Invoice Status': r.invoiceStatus || '', 'Payment Status': r.paymentStatus || '',
      'Payroll Available': r.payrollAvailable || 'No', 'Payroll Status': r.payrollStatus || '',
      'Payroll Account': r.payrollAccount || '',
      'Payroll Date Filed': r.payrollDateFiled ? getRawDateString(r.payrollDateFiled) : '',
      'Payroll Due Date': r.payrollDueDate ? getRawDateString(r.payrollDueDate) : '',
      'HST Available': r.hstAvailable || 'No', 'HST Period': r.hstPeriod || '',
      'HST Status': r.hstStatus || '',
      'HST Due Date': r.hstDueDate ? getRawDateString(r.hstDueDate) : '',
      'HST Date Filed': r.hstDateFiled ? getRawDateString(r.hstDateFiled) : '',
      'HST Invoice Status': r.hstInvoiceStatus || '', 'HST Notes': r.hstNotes || '',
      'Details': r.details || '', 'To Do List': r.todoList || '', 'Notes': r.notes || '',
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Corporate Tax Data');
    const ts = new Date().toISOString().replace(/T/, '_').replace(/:/g, '-').split('.')[0];
    XLSX.writeFile(wb, `Corporate_Tax_Data_${ts}.xlsx`);
  };

  // Computed stats
  const totalCount          = records.length;
  const completedCount      = records.filter(r => r.status === 'Completed').length;
  const nonCompletedCount   = totalCount - completedCount;
  const paymentPendingCount = records.filter(r => r.status === 'Payment Pending').length;
  const completionPct       = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const assignedToMeCount   = records.filter(r => r.assignedTo === account?.username).length;
  const hstCount            = records.filter(r => r.hstAvailable === 'Yes').length;
  const payrollCount        = records.filter(r => r.payrollAvailable === 'Yes').length;

  const activeStatusKey = activeTab === 'HST Filing' ? 'hstStatus' : activeTab === 'Payroll' ? 'payrollStatus' : 'status';
  const activeDueDateKey = activeTab === 'HST Filing' ? 'hstDueDate' : activeTab === 'Payroll' ? 'payrollDueDate' : 'dueDate';

  return (
    <TaxPortalLayout
      title="Corporate Tax"
      description="Use your Oriental Biz account to access corporate tax records."
      taxEntries={dialerRecords}
      portalState={portalState}
    >
      {account ? (
        <>
          {/* ══════════════ STAT CARDS ══════════════ */}
          <div className="row g-3 mb-4">

            {/* Non-completed */}
            <div className="col-6 col-lg-3">
              <div className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card">
                <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#f59e0b,#d97706)', boxShadow: '2px 0 10px rgba(245,158,11,0.3)' }} />
                <div className="d-flex align-items-center gap-3 position-relative z-1">
                  <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#fef3c7,#fde68a)' }}>
                    <FaFolderOpen size={20} style={{ color: '#d97706' }} />
                  </div>
                  <div>
                    <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>Non-completed</small>
                    <h2 className="mb-0 fw-bold" style={{ fontSize: '1.85rem', lineHeight: 1.1 }}>{nonCompletedCount}</h2>
                  </div>
                </div>
                <div className="mt-3 position-relative z-1">
                  <span className="badge rounded-pill fw-medium" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', fontSize: '0.68rem' }}>Action needed</span>
                </div>
              </div>
            </div>

            {/* Completed + progress */}
            <div className="col-6 col-lg-3">
              <div className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card">
                <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#22c55e,#15803d)', boxShadow: '2px 0 10px rgba(34,197,94,0.3)' }} />
                <div className="d-flex align-items-center gap-3 position-relative z-1">
                  <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#dcfce7,#bbf7d0)' }}>
                    <FaCheckCircle size={20} style={{ color: '#15803d' }} />
                  </div>
                  <div>
                    <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>Completed</small>
                    <h2 className="mb-0 fw-bold" style={{ fontSize: '1.85rem', lineHeight: 1.1 }}>
                      {completedCount} <span className="fs-6 text-muted fw-normal">/ {totalCount}</span>
                    </h2>
                  </div>
                </div>
                <div className="position-relative z-1">
                  <div className="completion-bar-track">
                    <div className="completion-bar-fill" style={{ width: `${completionPct}%` }} />
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.68rem' }}>{completionPct}% done</small>
                </div>
              </div>
            </div>

            {/* Payment Pending */}
            <div className="col-6 col-lg-3">
              <div className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card">
                <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#ef4444,#b91c1c)', boxShadow: '2px 0 10px rgba(239,68,68,0.3)' }} />
                <div className="d-flex align-items-center gap-3 position-relative z-1">
                  <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#fee2e2,#fecaca)' }}>
                    <FaExclamationCircle size={20} style={{ color: '#b91c1c' }} />
                  </div>
                  <div>
                    <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>Payment Pending</small>
                    <h2 className="mb-0 fw-bold" style={{ fontSize: '1.85rem', lineHeight: 1.1 }}>{paymentPendingCount}</h2>
                  </div>
                </div>
                <div className="mt-3 position-relative z-1">
                  <span className="badge rounded-pill fw-medium" style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', fontSize: '0.68rem' }}>Awaiting payment</span>
                </div>
              </div>
            </div>

            {/* Services snapshot */}
            <div className="col-6 col-lg-3">
              <div className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white d-flex flex-column hover-lift stat-card position-relative overflow-hidden">
                <span className="stat-accent" style={{ background: 'linear-gradient(180deg,#6366f1,#4338ca)', boxShadow: '2px 0 10px rgba(99,102,241,0.3)' }} />
                <div className="d-flex align-items-center gap-2 mb-3 position-relative z-1">
                  <div className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 34, height: 34, background: 'linear-gradient(135deg,#ede9fe,#ddd6fe)' }}>
                    <FaBuilding size={15} style={{ color: '#4338ca' }} />
                  </div>
                  <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>Services snapshot</small>
                </div>
                <div className="flex-grow-1 position-relative z-1">
                  {[
                    { label: 'Total records', val: totalCount, color: '#6366f1' },
                    { label: 'HST clients',   val: hstCount,   color: '#0e7490' },
                    { label: 'Payroll clients', val: payrollCount, color: '#15803d' },
                    { label: 'Assigned to me', val: assignedToMeCount, color: '#b45309' },
                  ].map(({ label, val, color }) => (
                    <div key={label} className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-muted" style={{ fontSize: '0.76rem' }}>{label}</span>
                      <span className="fw-bold" style={{ fontSize: '0.82rem', color }}>{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <style>{`
            .hover-lift { transition: transform 0.2s ease, box-shadow 0.2s ease; }
            .hover-lift:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(15,23,42,0.10) !important; }
            .corp-tab-btn {
              display: inline-flex; align-items: center; gap: 7px;
              padding: 8px 20px; border-radius: 999px;
              border: 1.5px solid #e2e8f0; background: #fff;
              color: #64748b; font-size: 0.82rem; font-weight: 600;
              cursor: pointer; transition: all 0.18s ease;
              box-shadow: 0 1px 3px rgba(15,23,42,0.05);
            }
            .corp-tab-btn:hover:not(.active-tab) { background: #f8fafc; border-color: #cbd5e1; color: #0f172a; }
            .corp-tab-btn.active-tab {
              background: linear-gradient(135deg, #2563eb, #1d4ed8);
              border-color: transparent; color: #fff;
              box-shadow: 0 4px 14px rgba(37,99,235,0.30);
            }
            .corp-tab-btn .tab-count {
              font-size: 0.7rem; padding: 2px 8px; border-radius: 999px;
              background: rgba(255,255,255,0.25); font-weight: 700;
            }
            .corp-tab-btn:not(.active-tab) .tab-count {
              background: #f1f5f9; color: #374151;
            }
            .table-modern tbody tr { transition: background-color 0.15s ease; }
            .table-modern tbody tr:hover { background-color: #f8fafc; }
            .table-modern thead th { position: sticky; top: 0; z-index: 2; user-select: none; background: #fff; }
            .table-header-sep { border-bottom: 2px solid #e2e8f0 !important; }
          `}</style>

          {/* ── Pill Tabs with counts ── */}
          <div className="d-flex align-items-center gap-2 mb-4 flex-wrap">
            {TABS.map(({ key, label }) => {
              const count = key === 'HST Filing'
                ? records.filter(r => r.hstAvailable === 'Yes').length
                : key === 'Payroll'
                ? records.filter(r => r.payrollAvailable === 'Yes').length
                : records.length;
              return (
                <button
                  key={key}
                  className={`corp-tab-btn${activeTab === key ? ' active-tab' : ''}`}
                  onClick={() => setActiveTab(key)}
                >
                  {label}
                  <span className="tab-count">{count}</span>
                </button>
              );
            })}
          </div>

          {/* ── Toolbar ── */}
          <TaxPortalToolbar
            searchPlaceholder="Search business, contact, status..."
            searchText={searchText}
            onSearchChange={setSearchText}
            statusOptions={statusOptions}
            filterStatus={filterStatus}
            onFilterStatusChange={setFilterStatus}
            assignedToOptions={assignedToOptions}
            filterAssignedTo={filterAssignedTo}
            onFilterAssignedToChange={setFilterAssignedTo}
            dueDateOptions={dueDateOptions}
            filterDueDate={filterDueDate}
            onFilterDueDateChange={setFilterDueDate}
            pageSize={pageSize}
            onPageSizeChange={(size) => { setPageSize(size); setPageIndex(0); }}
            onAdd={handleOpenDrawerForAdd}
            addLabel="Add Corporate"
            onExport={handleExportToExcel}
            disableExport={sortedData.length === 0}
            onRefresh={fetchRecords}
            isRefreshing={loading}
          />

          <div className="position-relative" style={{ minHeight: '350px' }}>

            {/* ══════════════ MOBILE CARDS ══════════════ */}
            <div className="d-block d-md-none">
              {loading ? (
                <div className="d-flex flex-column gap-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="card border-0 rounded-4" style={{ overflow: 'hidden' }}>
                      <div className="skeleton-cell" style={{ height: 5, borderRadius: 0 }} />
                      <div className="card-body p-3">
                        <div className="d-flex gap-2 align-items-center mb-3">
                          <span className="skeleton-cell" style={{ width: 36, height: 36, borderRadius: '50%' }} />
                          <span className="skeleton-cell" style={{ width: 130, height: 14 }} />
                        </div>
                        <span className="skeleton-cell" style={{ width: '100%', height: 32, borderRadius: 8 }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : pageData.length === 0 ? (
                <div className="card border-0 rounded-4 glass-card">
                  <div className="empty-state-wrap">
                    <div className="empty-state-icon"><FaInbox size={28} /></div>
                    <p className="mb-1 fw-semibold text-dark">No corporate records found</p>
                    <p className="mb-3 text-muted small text-center">Try adjusting your filters or search term.</p>
                    <button className="btn btn-sm btn-primary rounded-pill px-4" onClick={handleOpenDrawerForAdd}>
                      <FaUserPlus className="me-2" size={12} /> Add Corporate
                    </button>
                  </div>
                </div>
              ) : (
                pageData.map((record) => {
                  const recordId   = getId(record);
                  const statusMeta = getStatusMeta(activeTab === 'HST Filing' ? record.hstStatus : activeTab === 'Payroll' ? record.payrollStatus : record.status);
                  const avatarColor = getAvatarColor(record.businessName || '');
                  return (
                    <div key={recordId} className="card mb-3 mobile-record-card glass-card border-0 rounded-4 position-relative">
                      <div style={{ height: 5, background: statusMeta.color, borderRadius: '1rem 1rem 0 0' }} />
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <div className="name-avatar flex-shrink-0" style={{ backgroundColor: avatarColor.bg, color: avatarColor.text, width: 36, height: 36, fontSize: 12 }}>
                              {getInitials(record.businessName || '')}
                            </div>
                            <div>
                              <h6 className="mb-0 fw-bold">{record.businessName}</h6>
                              {record.contactName && <div className="small text-muted">{record.contactName}</div>}
                              {activeTab === 'Summary' && record.mobile && (
                                <button className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => { setDialNumber(record.mobile); setIsDialerOpen(true); }}>
                                  <span className="d-flex align-items-center gap-1 text-muted" style={{ fontSize: '0.75rem' }}><FaPhoneAlt size={9} /> {formatPhoneNumber(record.mobile)}</span>
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="dropdown">
                            <button className="action-menu-trigger dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-boundary="window" aria-label="Actions" title="Actions">
                              <FaEllipsisV size={13} />
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                              <li><button className="dropdown-item" onClick={() => handleOpenDrawerForEdit(record)}><span className="d-flex align-items-center gap-2"><FaEdit /> Edit Details</span></button></li>
                              <li><button className="dropdown-item" onClick={() => handleOpenActionItems(record)}><span className="d-flex align-items-center gap-2"><FaClipboardList /> Action Items</span></button></li>
                              <li><button className="dropdown-item" onClick={() => navigate('/invoice', { state: { customer: record, taxType: 'Corporate' } })}><span className="d-flex align-items-center gap-2"><FaFileInvoiceDollar /> Create Invoice</span></button></li>
                              <li><button className="dropdown-item" onClick={() => setEmailModalConfig({ customer: record, action: 'requestDocument', taxType: 'Corporate' })}><span className="d-flex align-items-center gap-2"><FaFolderOpen /> Request Document</span></button></li>
                              <li><button className="dropdown-item" onClick={() => setEmailModalConfig({ customer: record, action: 'sendConfirmation', taxType: 'Corporate' })}><span className="d-flex align-items-center gap-2"><FaPaperPlane /> Send Confirmation</span></button></li>
                            </ul>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <StatusBadge status={activeTab === 'HST Filing' ? (record.hstStatus || 'Unknown') : activeTab === 'Payroll' ? (record.payrollStatus || 'Unknown') : (record.status || 'Unknown')} />
                          <DueDatePill dueDate={activeTab === 'HST Filing' ? record.hstDueDate : activeTab === 'Payroll' ? record.payrollDueDate : record.dueDate} status={record.status} />
                        </div>
                        <select className="form-select form-select-sm glass-input shadow-sm rounded-3 py-2" value={record.assignedTo || ''} disabled={isSaving} onChange={(e) => handleAssignedToChange(record, e.target.value)}>
                          <option value="">Unassigned</option>
                          {assignedToOptions.map(email => <option key={email} value={email}>{email}</option>)}
                        </select>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* ══════════════ DESKTOP TABLE ══════════════ */}
            <div className="table-responsive d-none d-md-block rounded-4 shadow-sm bg-white" style={{ minHeight: '350px' }}>
              <table className="table table-modern table-hover align-middle mb-0" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '14%' }} />
                  {activeTab === 'Summary' && <col style={{ width: '12%' }} />}
                  <col style={{ width: activeTab === 'Summary' ? '13%' : '16%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: activeTab === 'Summary' ? '9%' : '10%' }} />
                  <col style={{ width: '8%' }} />
                </colgroup>
                <thead>
                  <tr className="table-header-sep text-muted text-uppercase" style={{ fontSize: '0.72rem', fontWeight: 600 }}>
                    {/* NAME_TH_PL = CELL_PS(16) + AVATAR_SIZE(32) + AVATAR_GAP(8) = 56px */}
                    <th className="py-3 border-0" style={{ paddingLeft: NAME_TH_PL, cursor: 'pointer' }} onClick={() => handleSort('businessName')}>
                      Business <SortIcon active={sortKey === 'businessName'} asc={sortAsc} />
                    </th>
                    <th className="py-3 d-none d-md-table-cell border-0" style={{ cursor: 'pointer' }} onClick={() => handleSort('contactName')}>Contact <SortIcon active={sortKey === 'contactName'} asc={sortAsc} /></th>
                    {activeTab === 'Summary' && (
                      <th className="py-3 d-none d-md-table-cell border-0" style={{ cursor: 'pointer' }} onClick={() => handleSort('mobile')}>Mobile <SortIcon active={sortKey === 'mobile'} asc={sortAsc} /></th>
                    )}
                    <th className="py-3 border-0" style={{ cursor: 'pointer' }} onClick={() => handleSort(activeStatusKey)}>Status <SortIcon active={sortKey === activeStatusKey} asc={sortAsc} /></th>
                    <th className="py-3 border-0" style={{ cursor: 'pointer' }} onClick={() => handleSort('assignedTo')}>Assigned To <SortIcon active={sortKey === 'assignedTo'} asc={sortAsc} /></th>
                    <th className="py-3 d-none d-md-table-cell border-0" style={{ cursor: 'pointer' }} onClick={() => handleSort(activeDueDateKey)}>Due Date <SortIcon active={sortKey === activeDueDateKey} asc={sortAsc} /></th>
                    <th className="py-3 border-0">Action</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {loading ? (
                    <SkeletonRows count={8} cols={activeTab === 'Summary' ? 7 : 6} />
                  ) : pageData.length === 0 ? (
                    <tr>
                      <td colSpan={activeTab === 'Summary' ? 7 : 6}>
                        <div className="empty-state-wrap">
                          <div className="empty-state-icon"><FaInbox size={28} /></div>
                          <p className="mb-1 fw-semibold text-dark">No corporate records found</p>
                          <p className="mb-3 text-muted small text-center" style={{ maxWidth: 320 }}>
                            No records match your current filters. Try a different search, status, or assignee.
                          </p>
                          <button className="btn btn-sm btn-primary rounded-pill px-4" onClick={handleOpenDrawerForAdd}>
                            <FaUserPlus className="me-2" size={12} /> Add Corporate
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    pageData.map((record) => {
                      const recordId     = getId(record);
                      const statusField  = activeTab === 'HST Filing' ? record.hstStatus : activeTab === 'Payroll' ? record.payrollStatus : record.status;
                      const dueDateField = activeTab === 'HST Filing' ? record.hstDueDate : activeTab === 'Payroll' ? record.payrollDueDate : record.dueDate;
                      return (
                        <React.Fragment key={recordId}>
                          <tr>
                            {/* ps-3 (16px) + avatar (32px) + gap-2 (8px) = 56px — matches th paddingLeft */}
                            <td className="ps-3" style={{ overflow: 'hidden' }}>
                              <div className="d-flex align-items-center gap-2">
                                <BizAvatar name={record.businessName || ''} />
                                <div style={{ minWidth: 0 }}>
                                  <div className="fw-semibold text-dark text-truncate" style={{ fontSize: '0.875rem' }} title={record.businessName}>{record.businessName}</div>
                                  {record.businessNumber && (
                                    <div className="text-muted text-truncate" style={{ fontSize: '0.72rem' }} title={`BN: ${formatBusinessNumber(record.businessNumber)}`}>BN: {formatBusinessNumber(record.businessNumber)}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="d-none d-md-table-cell" style={{ overflow: 'hidden' }}>
                              <span className="text-truncate d-block" style={{ fontSize: '0.82rem' }} title={record.contactName}>{record.contactName || <span className="text-muted">—</span>}</span>
                            </td>
                            {activeTab === 'Summary' && (
                              <td className="d-none d-md-table-cell">
                                {record.mobile ? (
                                  <button className="btn btn-link btn-sm p-0 text-decoration-none" style={{ fontSize: '0.82rem' }} onClick={() => { setDialNumber(record.mobile); setIsDialerOpen(true); }}>
                                    <span className="d-flex align-items-center gap-2"><FaPhoneAlt size={10} /> {formatPhoneNumber(record.mobile)}</span>
                                  </button>
                                ) : <span className="text-muted">—</span>}
                              </td>
                            )}
                            <td><StatusBadge status={statusField || 'Unknown'} /></td>
                            <td>
                              <select
                                className="form-select form-select-sm glass-input shadow-sm rounded-3 py-2"
                                value={record.assignedTo || ''}
                                disabled={isSaving}
                                onChange={(e) => handleAssignedToChange(record, e.target.value)}
                              >
                                <option value="">Unassigned</option>
                                {assignedToOptions.map(email => <option key={email} value={email}>{email}</option>)}
                              </select>
                            </td>
                            <td className="d-none d-md-table-cell">
                              <DueDatePill dueDate={dueDateField} status={statusField} />
                            </td>
                            <td>
                              <div className="dropdown">
                                <button className="action-menu-trigger dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-boundary="window" aria-label="Actions" title="Actions">
                                  <FaEllipsisV size={13} />
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end">
                                  <li><button className="dropdown-item" onClick={() => handleOpenDrawerForEdit(record)}><span className="d-flex align-items-center gap-2"><FaEdit /> Edit Details</span></button></li>
                                  <li><button className="dropdown-item" onClick={() => handleOpenActionItems(record)}><span className="d-flex align-items-center gap-2"><FaClipboardList /> Action Items</span></button></li>
                                  <li><button className="dropdown-item" onClick={() => navigate('/invoice', { state: { customer: record, taxType: 'Corporate' } })}><span className="d-flex align-items-center gap-2"><FaFileInvoiceDollar /> Create Invoice</span></button></li>
                                  <li><button className="dropdown-item" onClick={() => setEmailModalConfig({ customer: record, action: 'requestDocument', taxType: 'Corporate' })}><span className="d-flex align-items-center gap-2"><FaFolderOpen /> Request Document</span></button></li>
                                  <li><button className="dropdown-item" onClick={() => setEmailModalConfig({ customer: record, action: 'sendConfirmation', taxType: 'Corporate' })}><span className="d-flex align-items-center gap-2"><FaPaperPlane /> Send Confirmation</span></button></li>
                                </ul>
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ══════════════ PAGINATION ══════════════ */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 mt-4 px-1">
              <div className="d-flex align-items-center gap-2">
                <span
                  className="rounded-pill px-3 py-1 fw-semibold"
                  style={{ background: '#f1f5f9', color: '#374151', fontSize: '0.78rem', border: '1px solid #e2e8f0' }}
                >
                  {sortedData.length === 0 ? '0' : `${pageIndex * pageSize + 1}–${Math.min(sortedData.length, (pageIndex + 1) * pageSize)}`}
                  <span className="text-muted fw-normal"> of </span>
                  {sortedData.length}
                </span>
                <span className="text-muted" style={{ fontSize: '0.78rem' }}>records</span>
              </div>
              <div className="d-flex align-items-center gap-1">
                <button className="page-pill" disabled={pageIndex === 0} onClick={() => setPageIndex(p => Math.max(p - 1, 0))} aria-label="Previous page">
                  <FaChevronLeft size={10} /> Prev
                </button>
                {(() => {
                  if (totalPages <= 1) return null;
                  const items = [];
                  const addPage = (i) => items.push(
                    <button key={i} className={`page-pill${i === pageIndex ? ' active' : ''}`} onClick={() => setPageIndex(i)} aria-label={`Page ${i + 1}`} aria-current={i === pageIndex ? 'page' : undefined}>
                      {i + 1}
                    </button>
                  );
                  const addDots = (key) => items.push(<span key={key} className="page-dots">···</span>);
                  if (totalPages <= 7) {
                    for (let i = 0; i < totalPages; i++) addPage(i);
                  } else {
                    addPage(0);
                    if (pageIndex > 2) addDots('left');
                    for (let i = Math.max(1, pageIndex - 1); i <= Math.min(totalPages - 2, pageIndex + 1); i++) addPage(i);
                    if (pageIndex < totalPages - 3) addDots('right');
                    addPage(totalPages - 1);
                  }
                  return items;
                })()}
                <button className="page-pill" disabled={pageIndex >= totalPages - 1} onClick={() => setPageIndex(p => Math.min(p + 1, totalPages - 1))} aria-label="Next page">
                  Next <FaChevronRight size={10} />
                </button>
              </div>
            </div>
          </div>

          <CorporateCustomerDrawer
            isOpen={isDrawerOpen}
            onClose={handleCloseDrawer}
            customer={editingRecord}
            onSave={handleSaveRecord}
            onDelete={handleDeleteRecord}
            saving={isSaving}
            isAdmin={isAdmin}
            isDialerOpen={isDialerOpen}
          />
          <ActionItemsDrawer isOpen={!!actionItemsCustomer} onClose={() => setActionItemsCustomer(null)} customer={actionItemsCustomer} customerType="Corporate" account={account} msalInstance={msalInstance} isDialerOpen={isDialerOpen} />

          <Loader variant="overlay" show={isSaving && !loading} text="Saving changes..." />
        </>
      ) : (
        <div className="alert alert-info">Please sign in with your Oriental Biz account to view the corporate customer portal.</div>
      )}
    </TaxPortalLayout>
  );
};

export default CorporateTaxDataPage;
